import React from 'react';
import Button from '../ui/Button';
import { useI18n } from '../../context/I18nContext';

export type IntegrationCardStatus = 'loading' | 'error' | 'unavailable' | 'connected' | 'disconnected';

interface IntegrationCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  /** Neutral surface for brand logos (Google, etc.) */
  iconVariant?: 'accent' | 'brand';
  status: IntegrationCardStatus;
  email?: string | null;
  syncHint?: string | null;
  connectLabel: string;
  connectingLabel?: string;
  disconnectLabel: string;
  unavailableMessage?: string;
  errorMessage?: string;
  busy?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onRetry?: () => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  title,
  description,
  icon,
  iconVariant = 'accent',
  status,
  email,
  syncHint,
  connectLabel,
  connectingLabel,
  disconnectLabel,
  unavailableMessage,
  errorMessage,
  busy = false,
  onConnect,
  onDisconnect,
  onRetry,
}) => {
  const { t } = useI18n();
  const isConnected = status === 'connected';

  return (
    <div className="flex h-full flex-col rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${
            iconVariant === 'brand'
              ? 'bg-white ring-1 ring-gray-200/80'
              : 'bg-primary-50 text-primary-600'
          }`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-base font-semibold text-gray-900 leading-snug">{title}</h4>
            {status !== 'loading' && status !== 'error' && status !== 'unavailable' ? (
              <span
                className={`max-w-[55%] truncate rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  isConnected
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/70'
                    : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200/80'
                }`}
                title={isConnected && email ? email : undefined}
              >
                {isConnected
                  ? email || t('settings.integrationConnected')
                  : t('settings.integrationNotConnected')}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-gray-600 leading-snug">{description}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-1 flex-col gap-2">
        {syncHint ? <p className="text-xs text-gray-500">{syncHint}</p> : null}

        <div className="mt-auto w-full pt-1">
          {status === 'loading' ? (
            <Button type="button" variant="primary" className="w-full" loading disabled aria-busy="true">
              {connectLabel}
            </Button>
          ) : status === 'error' ? (
            <div className="w-full space-y-2">
              <p className="text-sm text-gray-600">{errorMessage}</p>
              {onRetry ? (
                <Button type="button" variant="secondary" className="w-full" onClick={onRetry}>
                  {t('common.retry')}
                </Button>
              ) : null}
            </div>
          ) : status === 'unavailable' ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
              {unavailableMessage}
            </p>
          ) : isConnected ? (
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={onDisconnect}
              loading={busy}
              disabled={busy || !onDisconnect}
            >
              {disconnectLabel}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={onConnect}
              loading={busy}
              disabled={busy || !onConnect}
            >
              {busy && connectingLabel ? connectingLabel : connectLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationCard;
